import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { getApplicationByToken } from '../services/certifiedNetworkService';

/**
 * Pagina per riprendere la compilazione tramite link temporaneo
 */
const ResumeCertifiedNetworkPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const toast = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [application, setApplication] = useState<any>(null);

  useEffect(() => {
    if (token) {
      loadApplication();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [token]);

  const loadApplication = async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await getApplicationByToken(token);
      
      if (response.success) {
        setApplication(response.application);
        toast.success('Domanda caricata con successo!');

        // Reindirizza alla pagina di compilazione con i dati caricati E il token
        setTimeout(() => {
          navigate('/rete-certificata/iscrizione', {
            state: {
              applicationId: response.application._id,
              editToken: token, // Passa il token per permettere modifiche
              resumeMode: true
            }
          });
        }, 2000);
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Link non valido o scaduto';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="xl" />
          <p className="mt-4 text-gray-600">Caricamento domanda in corso...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">
            Link Non Valido
          </h1>
          
          <p className="text-gray-600 text-center mb-6">
            {error}
          </p>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium mb-1">Possibili cause:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Il link è scaduto (validità 72 ore)</li>
                  <li>Il link è già stato utilizzato</li>
                  <li>Il link non è corretto</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => navigate('/rete-certificata/iscrizione')}
              className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Nuova Domanda
            </button>
            
            <button
              onClick={() => navigate('/rete-certificata')}
              className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Torna alla Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        
        <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">
          Domanda Trovata!
        </h1>
        
        <p className="text-gray-600 text-center mb-6">
          Reindirizzamento alla compilazione in corso...
        </p>

        {application && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-600 mb-2">
              <strong>Studio:</strong> {application.studioData?.ragioneSociale}
            </p>
            <p className="text-sm text-gray-600">
              <strong>P.IVA:</strong> {application.partitaIVA}
            </p>
          </div>
        )}

        <div className="flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-gray-300 border-t-green-600 rounded-full animate-spin"></div>
        </div>
      </div>
    </div>
  );
};

export default ResumeCertifiedNetworkPage;

